import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { LiveProvider } from './context/LiveContext';
import { ToastProvider } from './components/common/Toast';
import { Layout } from './components/layout/Layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { LoginPage } from './components/auth/LoginPage';
import { RegisterPage } from './components/auth/RegisterPage';
import { ForgotPasswordPage } from './components/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './components/auth/ResetPasswordPage';
import { GuestLoginPage } from './components/auth/GuestLoginPage';
import { Dashboard } from './components/dashboard/Dashboard';
import { ServiceDetail } from './components/service/ServiceDetail';
import { ServiceForm } from './components/service/ServiceForm';
import { GroupList } from './components/groups/GroupList';
import { NotificationList } from './components/notifications/NotificationList';
import { MaintenancePage } from './components/maintenance/MaintenancePage';
import { ConfigPage } from './components/config/ConfigPage';
import { StatusPagesPage } from './components/status-pages/StatusPagesPage';
import { StatusPageEditor } from './components/status-pages/StatusPageEditor';
import { ProfilePage } from './components/profile/ProfilePage';
import { UserListPage } from './components/admin/UserListPage';
import { GuestPasscodesPage } from './components/admin/GuestPasscodesPage';
import { AuditLogPage } from './components/admin/AuditLogPage';
import { IncidentsPage } from './components/incidents/IncidentsPage';
import { StatusPage } from './components/status/StatusPage';
import { NotFound } from './components/common/NotFound';
import { ReportsShell } from './components/reports/ReportsShell';
import { FleetReport } from './components/reports/FleetReport';
import { AvailabilityReport } from './components/reports/AvailabilityReport';
import { IncidentRateReport } from './components/reports/IncidentRateReport';
import { ResponseReport } from './components/reports/ResponseReport';
import { ServiceScorecard } from './components/reports/ServiceScorecard';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10000,
      retry: 1,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <SocketProvider>
            <LiveProvider>
            <ToastProvider>
              <BrowserRouter>
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/guest" element={<GuestLoginPage />} />
                  <Route path="/status" element={<StatusPage />} />
                  <Route path="/status/:slug" element={<StatusPage />} />
                  <Route path="/status/:slug/embed" element={<StatusPage embed />} />

                  {/* Protected routes */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<Layout />}>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/services/new" element={<ServiceForm />} />
                      <Route path="/services/:id" element={<ServiceDetail />} />
                      <Route path="/services/:id/edit" element={<ServiceForm />} />
                      <Route path="/incidents" element={<IncidentsPage />} />
                      <Route path="/reports" element={<ReportsShell />}>
                        <Route index element={<FleetReport />} />
                        <Route path="availability" element={<AvailabilityReport />} />
                        <Route path="incidents" element={<IncidentRateReport />} />
                        <Route path="response" element={<ResponseReport />} />
                      </Route>
                      <Route path="/reports/services/:id" element={<ServiceScorecard />} />
                      <Route path="/groups" element={<GroupList />} />
                      <Route path="/notifications" element={<NotificationList />} />
                      <Route path="/maintenance" element={<MaintenancePage />} />
                      {/* Bookmarks from before channels replaced webhooks. */}
                      <Route path="/webhooks" element={<Navigate to="/notifications" replace />} />
                      <Route path="/config" element={<ConfigPage />} />
                      <Route path="/status-pages" element={<StatusPagesPage />} />
                      <Route path="/status-pages/:id" element={<StatusPageEditor />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      {/* Anything else, inside the shell so there's a way out */}
                      <Route path="*" element={<NotFound />} />
                    </Route>
                  </Route>

                  {/* Admin routes */}
                  <Route element={<ProtectedRoute requiredPermission="users.view" />}>
                    <Route element={<Layout />}>
                      <Route path="/admin/users" element={<UserListPage />} />
                    </Route>
                  </Route>
                  <Route element={<ProtectedRoute requiredPermission="users.create" />}>
                    <Route element={<Layout />}>
                      <Route path="/admin/guest-passcodes" element={<GuestPasscodesPage />} />
                    </Route>
                  </Route>
                  <Route element={<ProtectedRoute requiredPermission="audit.view" />}>
                    <Route element={<Layout />}>
                      <Route path="/admin/audit" element={<AuditLogPage />} />
                    </Route>
                  </Route>
                </Routes>
              </BrowserRouter>
            </ToastProvider>
            </LiveProvider>
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
